"use client";

/**
 * Embedded host **shell** registry — the React half of the embedded-host
 * descriptor (the node-safe metadata half lives in `lib/embedded-hosts.ts`).
 * Maps each `EmbeddedSurface` to the shell component `HostProvider` mounts
 * around the app. Joined to the metadata + the server-side
 * `hostContextRenderers` only by the `EmbeddedSurface` enum, and kept in sync
 * by `lib/__tests__/embedded-hosts.test.ts`.
 *
 * Adding an embedded copilot = register its shell here (+ a metadata entry +
 * a renderer if it captures context).
 */

import type { EmbeddedSurface } from "@workspace/services/domain";
import type { FC, ReactNode } from "react";
import { FreshserviceShell } from "./freshservice/freshservice-shell";
import { OfficeShell } from "./office-addin/office-shell";
import { ProcoreShell } from "./procore/procore-shell";

type HostShell = FC<{ children: ReactNode }>;

// Exhaustive `Record` (not `Partial`): the compiler now requires every
// `EmbeddedSurface` to have a shell, so a new embedded surface can't be added
// to the descriptor without wiring its shell here. This is the compile-time
// completeness guard for the shell facet (see embedded-hosts.test.ts for the
// metadata/renderer facets).
const shellBySurface: Record<EmbeddedSurface, HostShell> = {
  "excel-add-in": OfficeShell,
  "outlook-add-in": OfficeShell,
  "word-add-in": OfficeShell,
  "procore-copilot": ProcoreShell,
  "freshservice-copilot": FreshserviceShell,
};

/** The shell component for a surface, or `undefined` if it isn't an embedded host. */
export function getEmbeddedHostShell(surface: string): HostShell | undefined {
  return shellBySurface[surface as EmbeddedSurface];
}
